import { useState, useDeferredValue } from 'react';
import { useTasksStore } from '../../stores/tasks';
import { useAuthStore } from '../../stores/auth';
import type { Task } from '../../lib/api';
import { isToday, isPast, parseISO, format } from 'date-fns';
import SegmentedControl from '../ui/SegmentedControl';
import { useMobile } from '../../hooks/useMobile';
import { AddTaskModal } from './TasksPanel';
import { groupTasks, groupStatus } from './taskGroups';

type SidebarFilter = 'all' | 'mine' | 'open' | 'done';

interface Props {
  onSelectTask: (task: Task) => void;
}

function dotStyle(status: Task['status']): React.CSSProperties {
  const base: React.CSSProperties = { width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 4 };
  if (status === 'open')        return { ...base, background: 'transparent', border: '2px solid var(--shell-xs)' };
  if (status === 'in_progress') return { ...base, background: 'var(--blue)', boxShadow: '0 0 5px rgba(55,138,221,.5)' };
  if (status === 'done')        return { ...base, background: 'var(--green)' };
  return base;
}

const STATUS_CHIP: Record<Task['status'], { label: string; color: string; background: string }> = {
  open:         { label: 'Open',        color: 'var(--shell-xs)', background: 'var(--hover-strong)' },
  in_progress:  { label: 'In Progress', color: 'var(--blue-soft)', background: 'rgba(55,138,221,.15)' },
  done:         { label: 'Done',        color: 'var(--green)', background: 'rgba(46,187,138,.15)' },
};

function dueText(due_at: string | null, status: Task['status']): { label: string; color: string } | null {
  if (!due_at) return null;
  if (status === 'done') return { label: 'Done', color: 'var(--teal-400)' };
  const date = parseISO(due_at);
  if (isToday(date)) return { label: 'Today', color: 'var(--amber)' };
  if (isPast(date))  return { label: 'Overdue', color: 'var(--red)' };
  return { label: format(date, 'd MMM'), color: 'var(--shell-xs)' };
}

export default function TasksSidebar({ onSelectTask }: Props) {
  const tasks = useTasksStore((s) => s.tasks);
  const [filter, setFilter] = useState<SidebarFilter>('mine');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const deferredSearch = useDeferredValue(search.toLowerCase().trim());
  const currentUser = useAuthStore((s) => s.user);
  const isMobile = useMobile();

  const filters: SidebarFilter[] = ['mine', 'open', 'done', 'all'];

  // Multi-assignee tasks collapse into one row, matching the board
  const visible = groupTasks(tasks)
    .filter((g) => {
      const status = groupStatus(g, currentUser?.id);
      if (filter === 'mine') return g.tasks.some((t) => t.assigned_to === currentUser?.id);
      if (filter === 'open') return status === 'open' || status === 'in_progress';
      if (filter === 'done') return status === 'done';
      return true;
    })
    .filter((g) => !isMobile || !deferredSearch || g.representative.title.toLowerCase().includes(deferredSearch))
    .sort((a, b) => new Date(b.representative.created_at).getTime() - new Date(a.representative.created_at).getTime());

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {showAddModal && <AddTaskModal onClose={() => setShowAddModal(false)} />}

      {/* Filter bar */}
      <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--shell-border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {isMobile && (
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks…"
            style={{
              background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 4,
              padding: '7px 10px', color: 'var(--text)', fontSize: '0.8rem',
              outline: 'none', width: '100%', boxSizing: 'border-box',
            }}
          />
        )}
        <SegmentedControl
          size="sm"
          fullWidth
          value={filter}
          onChange={setFilter}
          options={filters.map((f) => ({ value: f, label: f.charAt(0).toUpperCase() + f.slice(1) }))}
        />
      </div>

      {/* Task list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {visible.length === 0 && (
          <div style={{ color: 'var(--shell-xs)', fontSize: '0.75rem', padding: '24px 12px', textAlign: 'center' }}>
            No tasks
          </div>
        )}
        {visible.map((group) => {
          const task = group.representative;
          const status = groupStatus(group, currentUser?.id);
          const mine = group.tasks.find((t) => t.assigned_to === currentUser?.id);
          const due = dueText(task.due_at, status);
          const context = task.client_account_name ?? task.thread_name;
          const chip = STATUS_CHIP[status];
          return (
            <div
              key={group.key}
              onClick={() => onSelectTask(mine ?? task)}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                borderBottom: '1px solid var(--row-divider)',
                borderLeft: '2px solid transparent',
                display: 'flex', gap: 8, alignItems: 'flex-start',
                transition: 'background .12s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--hover)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              <div style={dotStyle(status)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '0.78rem', fontWeight: 500,
                  color: status === 'done' ? 'var(--shell-xs)' : 'var(--shell-muted)',
                  textDecoration: status === 'done' ? 'line-through' : 'none',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {task.title}
                </div>
                <div style={{ fontSize: '0.67rem', color: 'var(--shell-xs)', marginTop: 2, display: 'flex', gap: 6 }}>
                  {context && <span>{context}</span>}
                  {group.tasks.length > 1 && <span>{group.tasks.length} assignees</span>}
                  {due && <span style={{ color: due.color }}>{due.label}</span>}
                  <span style={{ opacity: 0.6 }}>
                    {format(parseISO(task.created_at), 'd MMM')}
                  </span>
                </div>
              </div>
              <span style={{
                flexShrink: 0, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em',
                textTransform: 'uppercase', padding: '3px 6px', borderRadius: 3,
                color: chip.color, background: chip.background,
              }}>
                {chip.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Add Task button (mobile — the board's footer button is hidden there) */}
      {isMobile && (
        <div style={{ padding: '10px', borderTop: '1px solid var(--shell-border)', flexShrink: 0 }}>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              width: '100%', padding: '10px 0', borderRadius: 5,
              background: 'var(--v1)', border: 'none', color: 'var(--text-on-accent)',
              fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
          >
            <span style={{ fontSize: '1rem', lineHeight: 1 }}>+</span> Add Task
          </button>
        </div>
      )}
    </div>
  );
}
